import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Waypoints } from 'lucide-react';
import { Card, Pill, RELATION_TONE, SectionHeader, Skeleton } from '../../../components/ui';
import { KIND_LABEL } from '../network/networkModel';
import { EntityAvatar } from '../network/ui';
import type { NetworkNode } from '../types';
import { egoLayout, subtitleOf, type ProfileTie } from './egoNetwork';
import { useProfileNetwork } from './useProfileNetwork';

const MAP = 244;
const CENTRE = MAP / 2;

const hrefOf = (node: NetworkNode) =>
  node.type === 'organization' ? `/organizations/${node.id}` : `/stakeholders/${node.id}`;

/**
 * The entity at the middle of its own ties.
 *
 * Organizations sit on one side and people on the other, so the shape says
 * something before any of it is read: a person with one employer and eleven
 * contacts looks different from a body with thirty members.
 */
const EgoMap: React.FC<{
  node: NetworkNode;
  ties: ProfileTie[];
  hovered: string | null;
  onHover: (id: string | null) => void;
}> = ({ node, ties, hovered, onHover }) => {
  const points = egoLayout(ties);
  const hiddenCount = ties.length - points.length;

  return (
    <svg
      viewBox={`0 0 ${MAP} ${MAP}`}
      className="h-[244px] w-[244px] flex-shrink-0"
      role="img"
      aria-label={`${node.label} and the entities they are tied to`}
    >
      <g transform={`translate(${CENTRE},${CENTRE})`}>
        {points.map((point) => {
          const tone = RELATION_TONE[point.link.type] ?? RELATION_TONE.ss;
          const lit = !hovered || hovered === point.id;
          return (
            <line
              key={`l:${point.id}`}
              x1={0}
              y1={0}
              x2={point.x}
              y2={point.y}
              stroke={tone.hex}
              strokeWidth={hovered === point.id ? 2 : 1.2}
              strokeOpacity={lit ? 0.5 : 0.15}
            />
          );
        })}
        {points.map((point) => {
          const person = point.node.type === 'stakeholder';
          const lit = !hovered || hovered === point.id;
          const size = person ? 9 : 8;
          return (
            <g
              key={point.id}
              transform={`translate(${point.x},${point.y})`}
              opacity={lit ? 1 : 0.3}
              onMouseEnter={() => onHover(point.id)}
              onMouseLeave={() => onHover(null)}
              style={{ cursor: 'pointer' }}
            >
              <a href={hrefOf(point.node)} aria-label={point.node.label}>
                {person ? (
                  <circle r={size} fill="#0ea5e9" stroke="#ffffff" strokeWidth={1.5} />
                ) : (
                  <rect
                    x={-size}
                    y={-size}
                    width={size * 2}
                    height={size * 2}
                    rx={3}
                    fill="#14b8a6"
                    stroke="#ffffff"
                    strokeWidth={1.5}
                  />
                )}
                <title>
                  {point.node.label} — {KIND_LABEL[point.link.kind]}
                </title>
              </a>
            </g>
          );
        })}
        <circle r={20} fill="#0a0a0a" />
        <text y={4} textAnchor="middle" fontSize={11} fontWeight={600} fill="#ffffff">
          {initials(node.label)}
        </text>
      </g>
      {hiddenCount > 0 && (
        <text x={CENTRE} y={MAP - 6} textAnchor="middle" fontSize={11} fill="#a3a3a3">
          +{hiddenCount} more
        </text>
      )}
    </svg>
  );
};

function initials(name: string): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  const letters = parts.length === 1 ? [parts[0][0]] : [parts[0][0], parts[parts.length - 1][0]];
  return letters.join('').toUpperCase();
}

const Row: React.FC<{
  node: NetworkNode;
  sub: string;
  note?: string;
  trailing?: React.ReactNode;
  onHover?: (hovering: boolean) => void;
  active?: boolean;
}> = ({ node, sub, note, trailing, onHover, active }) => (
  <Link
    to={hrefOf(node)}
    onMouseEnter={() => onHover?.(true)}
    onMouseLeave={() => onHover?.(false)}
    className={`flex min-w-0 items-center gap-2.5 rounded-xl px-2 py-1.5 transition-colors hover:bg-neutral-50 ${
      active ? 'bg-neutral-50' : ''
    }`}
  >
    <EntityAvatar node={node} size="sm" photo />
    <span className="min-w-0 flex-1">
      <span className="block truncate text-[13px] leading-tight text-ink">{node.label}</span>
      <span className="block truncate text-[11.5px] leading-tight text-neutral-500">{sub}</span>
      {note && <span className="mt-0.5 block truncate text-[11px] leading-tight text-neutral-400">{note}</span>}
    </span>
    {trailing}
  </Link>
);

/**
 * An entity's network, on its own profile: who it is tied to, and who to go
 * through to reach it.
 */
export const ProfileNetwork: React.FC<{
  id: string | undefined;
  className?: string;
}> = ({ id, className = '' }) => {
  const net = useProfileNetwork(id);
  const [hovered, setHovered] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  if (net.failed) return null;
  if (net.loading) {
    return (
      <section className={className}>
        <SectionHeader title="Network" />
        <Card padded>
          <Skeleton className="h-40 w-full" />
        </Card>
      </section>
    );
  }
  if (!net.node) return null;

  const routes = showAll ? net.routes : net.routes.slice(0, 6);

  return (
    <section className={className}>
      <SectionHeader
        title="Network"
        aside={
          <Link
            to={`/relationships?focus=${net.node.id}`}
            className="inline-flex items-center gap-1 text-[12.5px] text-neutral-500 transition-colors hover:text-ink"
          >
            Open in Network
            <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.75} />
          </Link>
        }
      />
      <Card padded>
        {net.ties.length === 0 ? (
          <div className="py-6 text-center">
            <span className="mx-auto inline-flex h-9 w-9 items-center justify-center rounded-full bg-neutral-100 text-neutral-400">
              <Waypoints className="h-4 w-4" strokeWidth={1.75} />
            </span>
            <p className="mt-2 text-[13px] text-ink">No ties recorded yet</p>
            <p className="mx-auto mt-1 max-w-sm text-[12.5px] leading-snug text-neutral-500">
              Roles and relationships recorded here will show as a network, with the people best placed to
              make an introduction.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start">
            <div className="flex flex-col items-center gap-2">
              <EgoMap node={net.node} ties={net.ties} hovered={hovered} onHover={setHovered} />
              {net.standing && (
                <p className="max-w-[244px] text-center text-[11.5px] leading-snug text-neutral-500">
                  {net.standing}
                  {net.secondDegree > 0 && ` · ${net.secondDegree} within two steps`}
                </p>
              )}
            </div>

            <div className="min-w-0 flex-1 space-y-4">
              {routes.length > 0 && (
                <div>
                  <p className="mb-1 flex items-center gap-2 text-[12px] text-neutral-500">
                    Best way in
                    {net.isGatekeeper && <Pill tone="amber">Holds a route others depend on</Pill>}
                  </p>
                  <div className="-mx-2">
                    {routes.map((route) => (
                      <Row
                        key={route.id}
                        node={route.node}
                        sub={subtitleOf(route.node)}
                        note={`${route.reason} · ${route.only ? 'the only contact recorded' : route.why}`}
                        active={hovered === route.id}
                        onHover={(h) => setHovered(h ? route.id : null)}
                        trailing={
                          route.influencePct >= 60 ? (
                            <span className="flex-shrink-0 text-[11px] tabular-nums text-neutral-400">
                              Top {Math.max(1, 100 - Math.round(route.influencePct))}%
                            </span>
                          ) : undefined
                        }
                      />
                    ))}
                  </div>
                </div>
              )}

              {net.routes.length > 6 && (
                <button
                  type="button"
                  onClick={() => setShowAll((v) => !v)}
                  className="inline-flex h-6 items-center rounded-full px-1.5 text-[12px] text-neutral-500 hover:text-ink"
                >
                  {showAll ? 'Show less' : `Show all ${net.routes.length}`}
                </button>
              )}
            </div>
          </div>
        )}
      </Card>
    </section>
  );
};
